import LogRocket from 'logrocket';
import {
	v4,
} from 'uuid';
import {
	Environment, getEnvironment,
} from '../environment/environment.util';
import {
	isInTest,
} from '../test';

interface SessionHistory {
	id: string;
	timestamp: number;
	expires: number;
}



let hasSetIdentity = false;
const sessionLogStorageKey = 'sessionLog';

/**
 * Gets the last 10 uuid stored in localstorage - which is sent to logrocket for customer current session - and the last 9.
 * This can be used in frontend for the user, so he/she can identify themselves when contacting TC if help is needed.
 * We can then search for the session in logrocket, to see what fails.
 * @returns SessionHistory[]
 */
export const getSessionHistory = (): SessionHistory[] => {
	const oldSessions = localStorage.getItem(sessionLogStorageKey) || '[]';
	return JSON.parse(oldSessions) as SessionHistory[] || [];
};

/**
 * The current session's ID
 */
export const getSessionId = (): string | undefined => getSessionHistory()[0]?.id;

export const setSessionIdExpires = (expires: number): void => {
	const history = getSessionHistory();
	if (history?.length && history[0].expires !== expires) {
		history[0].expires = expires;
		localStorage.setItem(sessionLogStorageKey, JSON.stringify(history));
	}
};

/**
 * Updates the session history log and returns the latest session id.
 * While updating the history, it also ensures that there are no more than 10 entries.
 * @return {string} session id
 */
const getAndUpdateSessionIdStorage = (): string => {
	const identifier = v4();
	const id = identifier.split('-')[0];
	if (localStorage) {
		const currentSession: SessionHistory = {
			id,
			timestamp: +new Date(),
			expires: +new Date() + 1000 * 60 * 60 * 12,
		};
		const log: SessionHistory[] = [
			currentSession,
			...getSessionHistory(),
		].slice(0, 10);

		localStorage.setItem(sessionLogStorageKey, JSON.stringify(log));
	}
	else {
		// eslint-disable-next-line no-console
		console.warn('Localstorage is not supported');
	}

	return id;
};

const isProduction = () => {
	if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
		return false;
	}
	else {
		return true;
	}
}

/**
 * Returns the correct id for the environment
 */
const getEnvironmentKey = () => {
	const base = 'msbcwv/takecargo-app';
	switch (getEnvironment()) {
		case Environment.PRODUCTION:
			return base;
		case Environment.QA:
			return `${base}-qa`;
		default:
			return `${base}-test`;
	}
};

/**
 * First time initialize environment of which to save data. Production has its own endpoint, and the rest is shared in test
 * @return {void}
 */
export const initLogRocket = (): void => {
	if (isInTest()) {
		return;
	}
	return  LogRocket.init(getEnvironmentKey());
};

/**
 * Initiates the log rocket identification
 * @return {void}
 */
export const setSessionToLogRocket = (): void => {
	if (!hasSetIdentity) {
		let id;
		const history = getSessionHistory();

		if (history.length && history[0].expires && history[0].expires > +new Date()) {
			// Making sure that we re-use the session id if it is still valid
			id = history[0].id;
		}
		else {
			id = getAndUpdateSessionIdStorage();
		}

		LogRocket.identify(
			id,
			{
				name: id,
				environment: isProduction() ? 'Production': 'Development',
				instance: 'TakeCargo PWA',
			}
		);
		hasSetIdentity = true;
	}
};